import {
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { searchIntegrationLog } from '../../api/business/endpoints';
import type { IntegrationLink, LogDirection, LogOutcome, LogSortKey } from '../../api/business/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

const OUTCOME_COLOUR: Record<LogOutcome, 'success' | 'warning' | 'error' | 'default'> = {
  OK: 'success',
  PARTIAL: 'warning',
  REJECTED: 'error',
  ERROR: 'error',
};

/** Every request with the shop either way (ADR-0007 §7) — the transparency the owner asked for. */
export const IntegrationLogTable = ({ link }: { link: IntegrationLink }) => {
  const { language, t } = useLanguage();
  const [flow, setFlow] = useState<LogDirection | ''>('');
  const [outcome, setOutcome] = useState<LogOutcome | ''>('');
  const [page, setPage] = useState(0);
  const sort = useSort<LogSortKey>('createdAt', 'desc', () => setPage(0));
  const query = useQuery({
    queryKey: ['integration-log', link.id, flow, outcome, page, sort.key, sort.direction],
    queryFn: () =>
      searchIntegrationLog(link.companyId, link.id, {
        flow: flow || undefined,
        outcome: outcome || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.items ?? [];

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          select
          size="small"
          label={t('log.flow')}
          value={flow}
          onChange={(e) => {
            setFlow(e.target.value as LogDirection | '');
            setPage(0);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          <MenuItem value="IN">{t('log.IN')}</MenuItem>
          <MenuItem value="OUT">{t('log.OUT')}</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label={t('log.outcome')}
          value={outcome}
          onChange={(e) => {
            setOutcome(e.target.value as LogOutcome | '');
            setPage(0);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(['OK', 'PARTIAL', 'REJECTED', 'ERROR'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`logOutcome.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="createdAt">
                {t('movements.when')}
              </SortableCell>
              <TableCell>{t('log.flow')}</TableCell>
              <SortableCell sort={sort} column="operation">
                {t('log.operation')}
              </SortableCell>
              <SortableCell sort={sort} column="outcome">
                {t('log.outcome')}
              </SortableCell>
              <SortableCell sort={sort} column="items" align="right">
                {t('log.items')}
              </SortableCell>
              <SortableCell sort={sort} column="durationMs" align="right">
                {t('log.duration')}
              </SortableCell>
              <TableCell>{t('log.detail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={7} />
            ) : (
              rows.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(entry.createdAt, language)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t(`log.${entry.direction}`)}
                    {entry.ip ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {entry.ip}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {entry.operation}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={OUTCOME_COLOUR[entry.outcome]}
                      label={`${t(`logOutcome.${entry.outcome}`)}${entry.httpStatus ? ` · ${entry.httpStatus}` : ''}`}
                    />
                  </TableCell>
                  <TableCell align="right">{formatNumber(entry.items, language)}</TableCell>
                  <TableCell align="right">
                    {entry.durationMs === null ? t('common.none') : `${entry.durationMs} ms`}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420, wordBreak: 'break-word' }}>
                    <Typography variant="body2" color="text.secondary">
                      {entry.detail ?? ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('log.empty')} detail={t('log.emptyDetail')} />
      ) : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
